import { format } from 'date-fns';
import JSZip from 'jszip';
import { formatInvoiceNumber } from './invoiceNumber';

const getFinancialYear = (date) => {
    const d = new Date(date);
    const month = d.getMonth();
    const year = d.getFullYear();
    if (month >= 3) { // April onwards
        return `${year}-${year + 1}`;
    } else {
        return `${year - 1}-${year}`;
    }
};

const formatInvoiceNumberSafe = (invNo, fy, cfg) => {
    try {
        if (typeof invNo === 'string' && /\d{4}-\d{2}\/\d+/.test(invNo)) {
            return invNo;
        }
        if (!fy || typeof fy !== 'string') {
            return invNo || 'N/A';
        }
        return formatInvoiceNumber(invNo, fy, cfg);
    } catch (e) {
        return invNo || 'N/A';
    }
};

const generateInvoiceData = (invoice, config) => {
    const items = Array.isArray(invoice.items) ? invoice.items : [];
    const cgst = Number(invoice.totals?.cgst || 0);
    const sgst = Number(invoice.totals?.sgst || 0);
    const total = Number(invoice.totals?.total || 0);

    let invoiceDate = '';
    if (invoice.invoiceDate && !isNaN(new Date(invoice.invoiceDate))) {
        invoiceDate = format(new Date(invoice.invoiceDate), 'dd/MM/yyyy');
    }

    // Calculate total assessors value (taxable value)
    const totalAssessableValue = items.reduce((sum, item) => {
        const qty = Number(item.qty || 1);
        const unitPrice = Number(item.unitPrice || item.amount || 0);
        return sum + (qty * unitPrice);
    }, 0);

    const isCreditNote = invoice.invoiceType === 'credit_note';
    const factor = isCreditNote ? -1 : 1;

    const cgstRateConfig = config && config.cgstRate !== undefined ? config.cgstRate : 0;
    const sgstRateConfig = config && config.sgstRate !== undefined ? config.sgstRate : 0;
    // Note: cgstRateConfig/sgstRateConfig are used as fallbacks inside map if needed, 
    // but looking at logic, we calculate rates mostly from totals or default.

    return {
        "TaxSch": "GST",
        "Version": "1.1",
        "TranDtls": {
            "TaxSch": "GST",
            "SupTyp": "B2B",
            "RegRev": "N",
            "EcmGstin": null,
            "IgstOnIntra": "N"
        },
        "DocDtls": {
            "Typ": isCreditNote ? "CRN" : "INV",
            "No": isCreditNote ? (invoice.creditNoteNumber || invoice.invoiceNumber) : formatInvoiceNumberSafe(invoice.invoiceNumber, invoice.financialYear || getFinancialYear(invoice.invoiceDate), config),
            "Dt": invoiceDate
        },
        "SellerDtls": {
            "Gstin": config.gstin || "29ABKCS7138K1ZM",
            "LglNm": config.companyName || "SPATIUM OFFICES PRIVATE LIMITED",
            "Addr1": config.companyAddress || "No.4,4-1, Bannerghatta main road, J P Nagar 3rd phase, Bangalore 560078",
            "Addr2": null,
            "Loc": "Bangalore",
            "Pin": 560078,
            "Stcd": "29",
            "Ph": "7996393939",
            "Em": "info@spatiumoffices.com"
        },
        "BuyerDtls": (() => {
            const fullAddr = invoice.client?.billToAddress || "";
            const addr1 = fullAddr.slice(0, 100);
            const addr2 = fullAddr.length > 100 ? fullAddr.slice(100) : null;
            const pinMatch = fullAddr.match(/\b\d{6}\b/);
            const pin = pinMatch ? Number(pinMatch[0]) : null;
            const buyerGstin = invoice.client?.gstin || "";
            const buyerStcd = (buyerGstin.match(/^[0-9]{2}/) || ["29"])[0];
            return {
                "Gstin": invoice.client?.gstin || "",
                "LglNm": invoice.client?.registeredName || invoice.client?.name || "",
                "Addr1": addr1,
                "Addr2": addr2,
                "Loc": "bengaluru",
                "Pin": pin,
                "Pos": "29", // Place of Supply state code
                "Stcd": buyerStcd,
                "Ph": null,
                "Em": null
            };
        })(),
        "ValDtls": {
            "AssVal": Number((totalAssessableValue * factor).toFixed(2)),
            "CgstVal": Number((cgst * factor).toFixed(2)),
            "SgstVal": Number((sgst * factor).toFixed(2)),
            "IgstVal": 0,
            "CesVal": 0,
            "StCesVal": 0,
            "Discount": 0,
            "OthChrg": 0,
            "RndOffAmt": 0,
            "TotInvVal": Number((total * factor).toFixed(2))
        },
        "ItemList": items.map((item, idx) => {
            const qty = Number(item.qty || 1);
            const unitPrice = Number(item.unitPrice || item.amount || 0);
            const totAmt = qty * unitPrice;
            const subtotalFromInvoice = Number(invoice.totals?.subtotal || 0);
            const cgstFromInvoice = Number(invoice.totals?.cgst || 0);
            const sgstFromInvoice = Number(invoice.totals?.sgst || 0);

            let cgstRate_item, sgstRate_item, gstRate;

            // Check for item-specific tax rate first (Adhoc mode)
            const taxVal = item.tax !== undefined && item.tax !== null ? item.tax : item.taxPercent;
            const hasSpecificTax = taxVal !== undefined && taxVal !== null && taxVal !== '';

            if (hasSpecificTax) {
                gstRate = Number(taxVal) || 0;
                cgstRate_item = gstRate / 2;
                sgstRate_item = gstRate / 2;
            }
            // Priority 2: Derive from invoice totals (Rent mode fallback)
            else if (subtotalFromInvoice > 0) {
                cgstRate_item = (cgstFromInvoice / subtotalFromInvoice) * 100;
                sgstRate_item = (sgstFromInvoice / subtotalFromInvoice) * 100;
                gstRate = cgstRate_item + sgstRate_item;
            }
            // Priority 3: Use global configuration fallback
            else {
                cgstRate_item = cgstRateConfig;
                sgstRate_item = sgstRateConfig;
                gstRate = cgstRate_item + sgstRate_item;
            }

            // Ensure values are numbers and handle edge cases
            cgstRate_item = Number(cgstRate_item) || 0;
            sgstRate_item = Number(sgstRate_item) || 0;
            gstRate = Number(gstRate) || 0;

            const cgstAmt = totAmt * (cgstRate_item / 100);
            const sgstAmt = totAmt * (sgstRate_item / 100);
            const totItemVal = totAmt + cgstAmt + sgstAmt;

            return {
                "SlNo": String(idx + 1),
                "PrdDesc": item.description || "Service",
                "IsServc": item.IsServc || (invoice.invoiceType === 'rent' ? "Y" : "N"),
                "HsnCd": item.hsn || "997212", // Default HSN if missing
                "Qty": invoice.invoiceType === 'rent' ? null : qty,
                "FreeQty": 0,
                "Unit": (item.uom || "OTH").toUpperCase(),
                "UnitPrice": Number((unitPrice * factor).toFixed(2)),
                "TotAmt": Number((totAmt * factor).toFixed(2)), // Gross Amount
                "Discount": 0,
                "PreTaxVal": 0,
                "AssAmt": Number((totAmt * factor).toFixed(2)), // Taxable Value
                "GstRt": Number(gstRate.toFixed(2)),
                "IgstAmt": 0,
                "CgstAmt": Number((cgstAmt * factor).toFixed(2)),
                "SgstAmt": Number((sgstAmt * factor).toFixed(2)),
                "CesRt": 0,
                "CesAmt": 0,
                "CesNonAdvlAmt": 0,
                "StateCesRt": 0,
                "StateCesAmt": 0,
                "StateCesNonAdvlAmt": 0,
                "OthChrg": 0,
                "TotItemVal": Number((totItemVal * factor).toFixed(2))
            };
        }),
        "RefDtls": {
            "InvRm": "NICGEPP2.0",
            ...(isCreditNote && invoice.originalInvoice ? {
                "PrecDocDtls": [{
                    "InvNo": formatInvoiceNumberSafe(invoice.originalInvoice.invoiceNumber, invoice.originalInvoice.financialYear || getFinancialYear(invoice.originalInvoice.invoiceDate), config),
                    "InvDt": invoice.originalInvoice.invoiceDate ? format(new Date(invoice.originalInvoice.invoiceDate), 'dd/MM/yyyy') : "",
                    "OthRefNo": null
                }]
            } : {})
        }
    };
};

export const downloadGSTJSON = async (invoices, config) => {
    // Ensure invoices is an array
    const invoiceArray = Array.isArray(invoices) ? invoices : [invoices];

    // Filter out cancelled
    const validInvoices = invoiceArray.filter(inv => !inv.isCancelled);

    if (validInvoices.length === 0) {
        throw new Error('No valid (non-cancelled) invoices to export.');
    }

    const allInvoicesData = validInvoices.map(inv => generateInvoiceData(inv, config));

    if (allInvoicesData.length === 1) {
        // Single Invoice -> ZIP containing JSON (Legacy behavior)
        // Actually, user might prefer direct JSON. But existing implementation used ZIP for single. 
        // Wait, the previous code had:
        // if (Array.isArray(invoicesArg) && invoicesArg.length === 1) { ... zip ... }
        // I will maintain this behavior for backward compatibility.

        const zip = new JSZip();
        const invoice = validInvoices[0];
        const data = allInvoicesData[0];

        let invoiceDateForFilename = 'unknown-date';
        if (invoice.invoiceDate && !isNaN(new Date(invoice.invoiceDate))) {
            invoiceDateForFilename = format(new Date(invoice.invoiceDate), 'yyyy-MM-dd');
        }

        // IMPORTANT: Single file content is [data] (array of 1) based on previous code: JSON.stringify([jsonData], null, 2)
        const invoiceFileName = `invoice-${invoice.financialYear || getFinancialYear(invoice.invoiceDate)}-${invoice.invoiceNumber}-${invoiceDateForFilename}.json`;
        zip.file(invoiceFileName, JSON.stringify([data], null, 2));

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${invoice.invoiceNumber}-${format(new Date(), 'yyyy-MM-dd')}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } else {
        // Multiple Invoices -> Single JSON Array file
        const jsonString = JSON.stringify(allInvoicesData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = `gst-invoices-batch-${allInvoicesData.length}-${format(new Date(), 'yyyy-MM-dd')}.json`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
};
